import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { JobQuickActions } from "../components/JobQuickActions";
import { MonthlyTeamGrid, styleFromColor } from "../components/MonthlyTeamGrid";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Inbox,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
  LayoutGrid,
  List,
  X,
  Filter,
} from "lucide-react";
import { fmtTime, to12h } from "../lib/format";
import { borrowedUntilLabel } from "../lib/teamMove";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";
import { FormField } from "../components/ui/form-field";
import { TimePicker12h } from "../components/ui/time-picker-12h";
import { SearchWithRecent } from "../components/SearchWithRecent";
import { useConfirm } from "../hooks/useConfirm";
import { useTeamWorkdays, isOffDay, OffDayIcon, confirmOffDay } from "../hooks/useTeamWorkdays";
import { useAuth } from "../auth/AuthContext";

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Helpers for team color contrast on schedule pills
function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function readableTextColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#0F172A";
  // perceived luminance — WCAG-ish
  const L = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return L > 0.62 ? "#0F172A" : "#FFFFFF";
}

export default function Schedule() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [view, setView] = useState("month"); // day|week|month
  const [statusFilter, setStatusFilter] = useState("all"); // all|pending|done
  const [cursor, setCursor] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState(() => {
    try {
      return localStorage.getItem("cm:scheduleTeamFilter") || "";
    } catch {
      return "";
    }
  }); // "" = todas as equipes
  function changeTeamFilter(v) {
    setTeamFilter(v);
    try {
      if (v) localStorage.setItem("cm:scheduleTeamFilter", v);
      else localStorage.removeItem("cm:scheduleTeamFilter");
    } catch {
      // ignore
    }
  }
  // Layout da visão "Mês": grade (calendário) ou lista (dias empilhados).
  // Lembra a última escolha por aparelho; sem preferência salva, no celular
  // (<1024px) começa em lista (mais confortável numa mão só).
  const [monthLayout, setMonthLayout] = useState(() => {
    try {
      const saved = localStorage.getItem("cm:monthLayout");
      if (saved === "grid" || saved === "list") return saved;
    } catch {
      // ignore
    }
    return typeof window !== "undefined" && window.innerWidth < 1024 ? "list" : "grid";
  });
  function changeMonthLayout(next) {
    setMonthLayout(next);
    try {
      localStorage.setItem("cm:monthLayout", next);
    } catch {
      // ignore
    }
  }
  const [pendingRequests, setPendingRequests] = useState([]);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [dropPicker, setDropPicker] = useState(null); // {req, targetDate}
  // Job quick-actions popover state — { job, anchorRef } when open.
  const [quickJob, setQuickJob] = useState(null);
  const onOpenQuick = useCallback((job, anchorRef) => {
    setQuickJob({ job, anchorRef });
  }, []);
  async function toggleDone(job) {
    const next = job.status === "finalizada" ? "agendada" : "finalizada";
    try {
      await api.patch(`/jobs/${job.id}`, { status: next });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao atualizar");
    }
  }

  // Deep-link vindo do Dashboard (widget de progresso): ?view=day&q=<equipe>
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    const team = params.get("team");
    const v = params.get("view");
    if (q) setSearchQuery(q);
    if (team) setTeamFilter(team);
    if (v === "day" || v === "week" || v === "month") setView(v);
  }, [location.search]);

  // Filtra jobs por cliente, equipe ou colaborador (membros) da equipe.
  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let base = teamFilter ? jobs.filter((j) => j.team_id === teamFilter) : jobs;
    if (statusFilter === "pending")
      base = base.filter(
        (j) => j.status !== "finalizada" && j.status !== "cancelada",
      );
    else if (statusFilter === "done")
      base = base.filter((j) => j.status === "finalizada");
    if (!q) return base;
    return base.filter((j) => {
      const c = clients.find((x) => x.id === j.client_id);
      const t = teams.find((x) => x.id === j.team_id);
      const memberNames = (t?.member_ids || [])
        .map((id) => users.find((u) => u.id === id)?.name)
        .filter(Boolean)
        .join(" ");
      const text = [
        c?.name,
        c?.address,
        c?.city,
        t?.name,
        memberNames,
        j.notes,
        j.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [jobs, clients, teams, users, searchQuery, teamFilter, statusFilter]);

  // Atalho do resumo: vai para a visão do Dia de hoje já filtrado por status.
  function focusToday(status) {
    setStatusFilter(status);
    setView("day");
    setCursor(new Date());
  }

  const range = useMemo(() => {
    const d = new Date(cursor);
    if (view === "day") {
      const s = new Date(d.setHours(0, 0, 0, 0));
      const e = new Date(s);
      e.setHours(23, 59, 59);
      return { from: s, to: e };
    }
    if (view === "week") {
      const start = new Date(d);
      start.setDate(d.getDate() - d.getDay());
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59);
      return { from: start, to: end };
    }
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
    return { from: start, to: end };
  }, [view, cursor]);

  const load = useCallback(async () => {
    const from = range.from.toISOString();
    const to = range.to.toISOString();
    const [j, c, t, u, br] = await Promise.all([
      api.get(`/jobs?from=${from}&to=${to}`),
      api.get("/clients"),
      api.get("/teams"),
      api.get("/collaborators"),
      api.get("/booking-requests?status=pending").catch(() => ({ data: [] })),
    ]);
    setJobs(j.data);
    setClients(c.data);
    setTeams(t.data);
    setUsers(u.data);
    setPendingRequests(br.data || []);
  }, [range.from, range.to]);
  useEffect(() => {
    load();
  }, [load]);

  // Resumo do dia de hoje (independe do mês/intervalo em tela).
  const [todayStats, setTodayStats] = useState({ total: 0, done: 0, pending: 0 });
  const loadTodayStats = useCallback(async () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    try {
      const { data } = await api.get(
        `/jobs?from=${start.toISOString()}&to=${end.toISOString()}`,
      );
      const list = (teamFilter ? data.filter((j) => j.team_id === teamFilter) : data).filter(
        (j) => j.status !== "cancelada",
      );
      const done = list.filter((j) => j.status === "finalizada").length;
      setTodayStats({ total: list.length, done, pending: list.length - done });
    } catch {
      setTodayStats({ total: 0, done: 0, pending: 0 });
    }
  }, [teamFilter]);
  useEffect(() => {
    loadTodayStats();
  }, [loadTodayStats, jobs]);

  // Se a equipe salva no filtro não existir mais, volta para "Todas".
  useEffect(() => {
    if (teamFilter && teams.length && !teams.some((t) => t.id === teamFilter)) {
      changeTeamFilter("");
    }
  }, [teams, teamFilter]);

  function onDropRequest(reqId, targetDate) {
    const req = pendingRequests.find((r) => r.id === reqId);
    if (!req) return;
    if (!teams.length) {
      toast.error(t("Crie pelo menos uma equipe antes de agendar"));
      return;
    }
    setDropPicker({ req, targetDate });
  }

  async function confirmDropPicker({ teamId, timeStr }) {
    if (!dropPicker) return;
    const { req, targetDate } = dropPicker;
    const team = teams.find((t) => t.id === teamId) || teams[0];
    const dt = new Date(targetDate);
    const m = (timeStr || "09:00").match(/^(\d{1,2}):(\d{2})/);
    dt.setHours(m ? Number(m[1]) : 9, m ? Number(m[2]) : 0, 0, 0);
    if (!(await confirmOffDay(confirm, team.id, dt, team.name))) return;
    try {
      const jobPayload = {
        client_id: req.client_id,
        team_id: team.id,
        scheduled_at: dt.toISOString(),
        notes: req.notes || "",
      };
      try {
        await api.post(`/jobs`, jobPayload);
      } catch (dupErr) {
        if (
          dupErr.response?.status === 409 &&
          dupErr.response?.data?.detail === "DUPLICATE_DAY"
        ) {
          const ok = window.confirm(
            t(
              "Já existe um atendimento para este cliente neste dia. Deseja mesmo repetir?",
            ),
          );
          if (!ok) return;
          await api.post(`/jobs?force=true`, jobPayload);
        } else {
          throw dupErr;
        }
      }
      await api.patch(`/booking-requests/${req.id}`, { status: "scheduled" });
      toast.success(
        `${t("Agendado:")} ${req.client_name} — ${dt.toLocaleString("en-US", {
          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        })} (${team.name})`,
      );
      setDropPicker(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Falha ao agendar"));
    }
  }

  function shift(n) {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + n);
    else if (view === "week") d.setDate(d.getDate() + 7 * n);
    else d.setMonth(d.getMonth() + n);
    setCursor(d);
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Operação")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <CalendarIcon size={26} /> {t("Agenda")}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-white border border-slate-200 rounded-lg p-1 flex">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`schedule-view-${v}`}
                className={`px-3 py-1.5 min-h-[40px] sm:min-h-0 text-sm rounded-md ${
                  view === v
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {v === "day" ? t("Dia") : v === "week" ? t("Semana") : t("Mês")}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => setCursor(new Date())}
            data-testid="schedule-today-btn"
            className="border-slate-300 font-semibold"
          >
            {t("Hoje")}
          </Button>
          <Button
            variant="outline"
            onClick={() => shift(-1)}
            data-testid="schedule-prev"
          >
            <ChevronLeft size={16} />
          </Button>
          <div className="text-sm font-medium px-2 min-w-[150px] text-center">
            {view === "month"
              ? `${t(MONTH_NAMES[cursor.getMonth()])} ${cursor.getFullYear()}`
              : `${range.from.toLocaleDateString("en-US")} — ${range.to.toLocaleDateString("en-US")}`}
          </div>
          <Button
            variant="outline"
            onClick={() => shift(1)}
            data-testid="schedule-next"
          >
            <ChevronRight size={16} />
          </Button>
          <Button
            onClick={() => setOpen(true)}
            data-testid="schedule-new-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-1" /> {t("Agendar")}
          </Button>
          <Link to="/schedule/month" data-testid="schedule-monthly-link">
            <Button
              variant="outline"
              className="border-[#249EEB] text-[#249EEB] hover:bg-[#249EEB]/10"
            >
              <CalendarIcon size={16} className="mr-1" /> {t("Calendário mensal")}
            </Button>
          </Link>
        </div>
      </div>

      {todayStats.total > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5"
          data-testid="today-summary"
        >
          <span className="text-sm font-bold text-blue-800 inline-flex items-center gap-1.5">
            <CalendarIcon size={15} /> {t("Hoje")}
          </span>
          <button
            type="button"
            onClick={() => focusToday("all")}
            data-testid="today-total-btn"
            className="text-sm text-slate-700 hover:text-blue-700 hover:underline underline-offset-2"
          >
            <strong data-testid="today-total">{todayStats.total}</strong>{" "}
            {todayStats.total === 1 ? t("limpeza") : t("limpezas")}
          </button>
          <button
            type="button"
            onClick={() => focusToday("done")}
            data-testid="today-done-btn"
            className="text-sm text-emerald-700 inline-flex items-center gap-1 hover:underline underline-offset-2"
          >
            <CheckCircle2 size={14} />
            <strong data-testid="today-done">{todayStats.done}</strong> {t("concluídas")}
          </button>
          <button
            type="button"
            onClick={() => focusToday("pending")}
            data-testid="today-pending-btn"
            className="text-sm text-amber-700 font-medium hover:underline underline-offset-2"
          >
            <strong data-testid="today-pending">{todayStats.pending}</strong> {t("pendentes")}
          </button>
        </div>
      )}

      {statusFilter !== "all" && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="active-status-filter">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Filter size={12} /> {t("Mostrando:")}
          </span>
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            data-testid="clear-status-filter"
            title={t("Remover filtro")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors hover:opacity-80 ${
              statusFilter === "pending"
                ? "text-amber-700 border-amber-200 bg-amber-50"
                : "text-emerald-700 border-emerald-200 bg-emerald-50"
            }`}
          >
            {statusFilter === "pending" ? t("Pendentes") : t("Concluídas")}
            <X size={13} />
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <SearchWithRecent
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t("Buscar por cliente, equipe, colaborador…")}
          testId="schedule-search"
          storageKey="cm:recent:schedule"
          className="flex-1"
        />
        <select
          value={teamFilter}
          onChange={(e) => changeTeamFilter(e.target.value)}
          data-testid="schedule-team-filter"
          aria-label={t("Filtrar por equipe")}
          className="cm-input sm:w-56"
        >
          <option value="">{t("Todas as equipes")}</option>
          {teams.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.name}
            </option>
          ))}
        </select>
      </div>

      {teamFilter &&
        (() => {
          const activeTeam = teams.find((tm) => tm.id === teamFilter);
          const st = activeTeam?.color ? styleFromColor(activeTeam.color) : null;
          return (
            <div
              className="mb-4 flex items-center gap-2 flex-wrap"
              data-testid="active-team-filter"
            >
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                <Filter size={12} /> {t("Filtrando por equipe:")}
              </span>
              <button
                type="button"
                onClick={() => changeTeamFilter("")}
                data-testid="clear-team-filter"
                title={t("Remover filtro")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors hover:opacity-80"
                style={{
                  color: st?.head || "#2563EB",
                  borderColor: st?.border || "#BFDBFE",
                  background: st?.bg || "#EFF6FF",
                }}
              >
                {activeTeam?.name || t("Equipe")}
                <span className="opacity-70 font-medium">
                  · {filteredJobs.length}{" "}
                  {filteredJobs.length === 1 ? t("limpeza") : t("limpezas")}
                </span>
                <X size={13} />
              </button>
            </div>
          );
        })()}

      <OrphanJobsBanner jobs={jobs} onCleaned={load} />

      {view === "month" && (
        <>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-xs text-slate-500 hidden sm:block">
              {t("Escolha como visualizar o mês")}
            </div>
            <div
              className="bg-white border border-slate-200 rounded-lg p-1 flex ml-auto"
              data-testid="month-layout-toggle"
            >
              <button
                onClick={() => changeMonthLayout("grid")}
                data-testid="month-layout-grid"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 min-h-[40px] sm:min-h-0 text-sm rounded-md transition-colors ${
                  monthLayout === "grid"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <LayoutGrid size={14} /> {t("Grade")}
              </button>
              <button
                onClick={() => changeMonthLayout("list")}
                data-testid="month-layout-list"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 min-h-[40px] sm:min-h-0 text-sm rounded-md transition-colors ${
                  monthLayout === "list"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <List size={14} /> {t("Lista")}
              </button>
            </div>
          </div>
          {monthLayout === "grid" ? (
            <MonthlyTeamGrid
              cursor={cursor}
              jobs={filteredJobs}
              clients={clients}
              teams={teams}
              onOpenQuick={onOpenQuick}
              onToggleDone={toggleDone}
            />
          ) : (
            <ListByDay
              range={range}
              jobs={filteredJobs}
              clients={clients}
              teams={teams}
              onOpenQuick={onOpenQuick}
              onMoved={load}
              onDropRequest={onDropRequest}
            />
          )}
        </>
      )}
      {view === "week" && (
        <ListByDay range={range} jobs={filteredJobs} clients={clients} teams={teams} onOpenQuick={onOpenQuick} onMoved={load} onDropRequest={onDropRequest} />
      )}
      {view === "day" && (
        <ListByDay
          range={range}
          jobs={filteredJobs}
          clients={clients}
          teams={teams}
          singleDay
          onOpenQuick={onOpenQuick}
          onMoved={load}
          onDropRequest={onDropRequest}
        />
      )}

      <BacklogPanel
        open={backlogOpen}
        onToggle={() => setBacklogOpen((v) => !v)}
        requests={pendingRequests}
      />

      <DropRequestPicker
        state={dropPicker}
        teams={teams}
        clients={clients}
        onClose={() => setDropPicker(null)}
        onConfirm={confirmDropPicker}
      />

      {quickJob && (
        <JobQuickActions
          job={quickJob.job}
          anchorRef={quickJob.anchorRef}
          teams={teams}
          onClose={() => setQuickJob(null)}
          onMutated={() => {
            setQuickJob(null);
            load();
          }}
        />
      )}

      <JobDialog
        open={open}
        onOpenChange={setOpen}
        clients={clients}
        teams={teams}
        users={users}
        defaultDate={cursor}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
    </AdminLayout>
  );
}

// Maps job status → bright accent color used as the left status bar
const STATUS_COLOR = {
  agendada: "#2563EB",      // blue
  deslocamento: "#F59E0B",  // amber
  andamento: "#8B5CF6",     // purple
  finalizada: "#10B981",    // emerald
  cancelada: "#EF4444",     // red
};

// Maps job status → tiny glyph rendered after the text (helps the operator
// scan job state at a glance without losing the team color).
const STATUS_GLYPH = {
  finalizada: "✓",
  andamento: "●",
  cancelada: "✕",
};

function JobPill({ job, clients, teams, onOpenQuick }) {
  const { t: tr } = useI18n();
  const ref = useRef(null);
  const c = clients.find((x) => x.id === job.client_id);
  const t = teams.find((x) => x.id === job.team_id);
  const live = job.status === "andamento";
  const cancelled = job.status === "cancelada";
  const teamColor = t?.color || "#2563EB";
  const textColor = readableTextColor(teamColor);
  const statusColor = STATUS_COLOR[job.status] || "#94A3B8";
  const glyph = STATUS_GLYPH[job.status];
  // Drag-and-drop bloqueado para jobs finalizados/em andamento/cancelados
  const canDrag = job.status === "agendada" || job.status === "deslocamento";
  const workdays = useTeamWorkdays();
  const offDay = !cancelled && isOffDay(workdays, job.team_id, job.scheduled_at);
  return (
    <button
      ref={ref}
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) return;
        e.dataTransfer.setData("text/job-id", job.id);
        e.dataTransfer.setData("text/job-scheduled-at", job.scheduled_at);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenQuick({ ...job, client_name: c?.name }, ref);
      }}
      data-testid={`cal-job-${job.id}`}
      title={`${c?.name || tr("Cliente")} — ${c?.address || ""}${t?.name ? ` · ${t.name}` : ""} — ${job.status}${canDrag ? ` · ${tr("arraste para mover")}` : ""}`}
      className={`group relative flex items-center w-full text-left max-w-full text-[11px] pl-2.5 pr-1.5 py-1 rounded-md overflow-hidden ${
        live ? "job-live" : ""
      } ${cancelled ? "line-through opacity-80" : ""} ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{
        backgroundColor: teamColor,
        color: textColor,
        borderLeft: `5px solid ${statusColor}`,
        borderRight: `1px solid ${teamColor}`,
        borderTop: `1px solid ${teamColor}`,
        borderBottom: `1px solid ${teamColor}`,
      }}
    >
      <span className="truncate flex-1 min-w-0">
        <strong>{fmtTime(job.scheduled_at)}</strong>{" "}
        {c?.name || tr("Cliente")}
        {t?.name ? ` · ${t.name}` : ""}
      </span>
      {offDay && (
        <span className="ml-1 bg-white rounded-full p-0.5" title={tr("Dia em que a equipe normalmente não trabalha")}>
          <OffDayIcon size={11} testId={`cal-offday-${job.id}`} />
        </span>
      )}
      {glyph && (
        <span
          className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[8px] font-bold flex-shrink-0"
          style={{ backgroundColor: statusColor, color: "#fff" }}
          aria-label={job.status}
        >
          {glyph}
        </span>
      )}
    </button>
  );
}

function OrphanJobsBanner({ jobs, onCleaned }) {
  const { t: tr } = useI18n();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [cleaning, setCleaning] = useState(false);
  const [count, setCount] = useState(0);

  // Detecção GLOBAL: consulta o backend por TODOS os agendamentos órfãos
  // (cliente OU equipe apagados), independente do mês/intervalo em tela.
  const refresh = useCallback(async () => {
    if (user?.role !== "admin") {
      setCount(0);
      return;
    }
    try {
      const { data } = await api.get("/jobs-orphans");
      setCount(data?.total || 0);
    } catch {
      setCount(0);
    }
  }, [user?.role]);

  // Re-verifica ao montar e sempre que a agenda recarregar (jobs mudam).
  useEffect(() => {
    refresh();
  }, [refresh, jobs]);

  async function handleClean() {
    const ok = await confirm({
      title: tr("Limpar agendamentos órfãos?"),
      description: tr("Esta ação apaga todos os agendamentos cujo cliente OU equipe não existe mais no sistema. Útil quando você apagou um cliente/equipe e ficaram limpezas fantasmas na agenda. Não pode ser desfeito."),
      destructive: true,
      actionLabel: tr("Limpar todos"),
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const { data } = await api.delete("/jobs-orphans");
      toast.success(`${data.deleted} ${tr("agendamento(s) órfão(s) removido(s)")}`);
      setCount(0);
      onCleaned && onCleaned();
      // Reconfirma o estado no backend após o reload.
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || tr("Falha ao limpar"));
    } finally {
      setCleaning(false);
    }
  }

  if (count === 0) return null;
  return (
    <div
      data-testid="orphan-jobs-banner"
      className="mb-4 cm-card p-3 bg-rose-50 border-2 border-rose-200 flex flex-wrap items-center justify-between gap-2"
    >
      <div className="text-sm text-rose-900 flex items-center gap-2">
        <Trash2 size={16} className="text-rose-600" />
        <span>
          <strong>{count}</strong> {tr("agendamento(s) órfão(s)")} —{" "}
          {tr("cliente ou equipe foi apagado mas os agendamentos ficaram na agenda.")}
        </span>
      </div>
      <Button
        size="sm"
        onClick={handleClean}
        disabled={cleaning}
        data-testid="orphan-clean-btn"
        className="bg-rose-600 hover:bg-rose-700"
      >
        {cleaning ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
        {tr("Limpar todos")} ({count})
      </Button>
    </div>
  );
}


function JobListRow({ job, clients, teams, onOpenQuick, onMutated, hideTeam = false }) {
  const { t: tr } = useI18n();
  const { user } = useAuth();
  const confirm = useConfirm();
  const ref = useRef(null);
  const [deleting, setDeleting] = useState(false);
  const c = clients.find((x) => x.id === job.client_id);
  const t = teams.find((x) => x.id === job.team_id);
  const live = job.status === "andamento";
  const cancelled = job.status === "cancelada";
  const canDrag = job.status === "agendada" || job.status === "deslocamento";
  const isAdmin = user?.role === "admin";
  const orphan = (!c && !!job.client_id) || (!t && !!job.team_id);
  const workdays = useTeamWorkdays();
  const offDay = !cancelled && isOffDay(workdays, job.team_id, job.scheduled_at);

  async function handleDelete(e) {
    e.stopPropagation();
    const ok = await confirm({
      title: tr("Excluir definitivamente?"),
      description: tr("Este dia e TODOS os próximos desta casa nesta equipe serão apagados do sistema. Casas já finalizadas ficam no histórico."),
      destructive: true,
      actionLabel: tr("Excluir tudo"),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/jobs/${job.id}`);
      toast.success(`${data?.deleted_count ?? 1} ${tr("atendimento(s) excluído(s)")}`);
      onMutated && onMutated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || tr("Falha ao excluir"));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      ref={ref}
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) return;
        e.dataTransfer.setData("text/job-id", job.id);
        e.dataTransfer.setData("text/job-scheduled-at", job.scheduled_at);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => {
        e.stopPropagation();
        onOpenQuick({ ...job, client_name: c?.name }, ref);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenQuick({ ...job, client_name: c?.name }, ref);
        }
      }}
      className={`w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100 border-l-4 ${
        live ? "job-live" : ""
      } ${cancelled ? "opacity-70" : ""} ${orphan ? "bg-rose-50 border-rose-200" : "bg-white"} ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
      style={{ borderLeftColor: orphan ? "#ef4444" : cancelled ? "#ef4444" : t?.color || "#2563EB" }}
      data-testid={`list-job-${job.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`font-mono font-semibold ${cancelled ? "line-through text-red-500" : "text-slate-700"}`}
        >
          {fmtTime(job.scheduled_at)}
        </div>
        <div className="min-w-0">
          <div
            className={`font-medium truncate flex items-center gap-1 ${cancelled ? "line-through text-red-500" : ""} ${orphan ? "text-rose-700" : ""}`}
          >
            {offDay && (
              <span title={tr("Dia em que a equipe normalmente não trabalha")}>
                <OffDayIcon size={14} testId={`list-offday-${job.id}`} />
              </span>
            )}
            {c?.name || (job.client_id ? tr("Cliente removido") : "—")}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {c?.address ? c.address : ""}
            {!hideTeam && (
              <>
                {c?.address ? " · " : ""}
                {tr("Equipe")}{" "}
                <span
                  className="font-semibold"
                  style={{ color: t?.color || (orphan ? "#dc2626" : "#2563EB") }}
                >
                  {t?.name || (job.team_id ? tr("Equipe removida") : "—")}
                </span>
              </>
            )}
            {borrowedUntilLabel(c) && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold text-[10px]">
                {tr("Emprestada até")} {borrowedUntilLabel(c)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <StatusBadge status={job.status} job={job} />
        <Link
          to={`/jobs/${job.id}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`list-job-edit-${job.id}`}
          title={tr("Editar")}
          className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        >
          <Pencil size={18} />
        </Link>
        {isAdmin && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            data-testid={`list-job-delete-${job.id}`}
            title={tr("Excluir")}
            className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
          </button>
        )}
      </div>
    </div>
  );
}

function MonthGrid({ cursor, jobs, clients, teams, onOpenQuick, onMoved, onDropRequest }) {
  const { t } = useI18n();
  const [dayDetail, setDayDetail] = useState(null); // { date, jobs }
  const [dragOverKey, setDragOverKey] = useState(null);
  const confirm = useConfirm();

  async function handleDrop(e, targetDate) {
    e.preventDefault();
    setDragOverKey(null);
    const jobId = e.dataTransfer.getData("text/job-id");
    const oldIso = e.dataTransfer.getData("text/job-scheduled-at");
    const reqId = e.dataTransfer.getData("text/booking-request-id");
    if (reqId) {
      onDropRequest && onDropRequest(reqId, targetDate);
      return;
    }
    if (!jobId || !oldIso) return;
    const old = new Date(oldIso);
    if (old.toDateString() === targetDate.toDateString()) return; // mesmo dia
    const next = new Date(targetDate);
    next.setHours(old.getHours(), old.getMinutes(), 0, 0);
    const mj = jobs.find((x) => x.id === jobId);
    const mt = teams.find((x) => x.id === mj?.team_id);
    if (!(await confirmOffDay(confirm, mj?.team_id, next, mt?.name))) return;
    try {
      await api.patch(`/jobs/${jobId}`, { scheduled_at: next.toISOString() });
      toast.success(
        `${t("Movido para")} ${next.toLocaleDateString("en-US", {
          weekday: "short",
          day: "2-digit",
          month: "short",
        })}`,
      );
      onMoved && onMoved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Falha ao mover"));
    }
  }
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startWd = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="cm-card overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {WD.map((w) => (
          <div
            key={w}
            className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            {t(w)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          if (!d)
            return (
              <div
                key={`empty-${i}`}
                className="cal-cell bg-slate-50/50 border-r border-b border-slate-100"
              />
            );
          const dayJobs = jobs
            .filter(
              (j) =>
                new Date(j.scheduled_at).toDateString() === d.toDateString(),
            )
            .sort((a, b) => {
              // Agrupa por equipe (nome da equipe asc) e, dentro da equipe,
              // ordena por horário.
              const ta = teams.find((t) => t.id === a.team_id)?.name || "ZZZ";
              const tb = teams.find((t) => t.id === b.team_id)?.name || "ZZZ";
              const cmp = ta.localeCompare(tb, "pt-BR");
              if (cmp !== 0) return cmp;
              return a.scheduled_at.localeCompare(b.scheduled_at);
            });
          const isToday = d.toDateString() === new Date().toDateString();
          const dayKey = d.toISOString().slice(0, 10);
          const isDragOver = dragOverKey === dayKey;
          return (
            <div
              key={d.toISOString()}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragOverKey !== dayKey) setDragOverKey(dayKey);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setDragOverKey(null);
              }}
              onDrop={(e) => handleDrop(e, d)}
              data-testid={`cal-cell-${dayKey}`}
              className={`cal-cell border-r border-b border-slate-100 p-2 transition-colors ${
                isToday ? "bg-blue-50/40 ring-2 ring-blue-500 ring-inset" : ""
              } ${isDragOver ? "bg-blue-100/70 ring-2 ring-blue-400 ring-inset" : ""}`}
            >
              <div className="flex items-center gap-1">
                {isToday ? (
                  <span
                    data-testid={`cal-today-${dayKey}`}
                    className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm"
                    title={t("Hoje")}
                  >
                    {d.getDate()}
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-slate-700">
                    {d.getDate()}
                  </span>
                )}
                {isToday && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                    {t("Hoje")}
                  </span>
                )}
              </div>
              <div className="mt-1 space-y-1">
                {dayJobs.slice(0, 3).map((j) => (
                  <JobPill
                    key={j.id}
                    job={j}
                    clients={clients}
                    teams={teams}
                    onOpenQuick={onOpenQuick}
                  />
                ))}
                {dayJobs.length > 3 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDayDetail({ date: d, jobs: dayJobs });
                    }}
                    data-testid={`cal-more-${d.toISOString().slice(0, 10)}`}
                    className="block w-full text-left text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline px-1"
                  >
                    {t("+{n} mais", { n: dayJobs.length - 3 })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <DayDetailDialog
        detail={dayDetail}
        onClose={() => setDayDetail(null)}
        clients={clients}
        teams={teams}
        onMutated={onMoved}
        onOpenQuick={(job, ref) => {
          // Fecha o dialog antes de abrir o popover de quick actions para
          // evitar overlay roubando o foco.
          setDayDetail(null);
          setTimeout(() => onOpenQuick(job, ref), 50);
        }}
      />
    </div>
  );
}

function DayDetailDialog({ detail, onClose, clients, teams, onOpenQuick, onMutated }) {
  const { t: tr } = useI18n();
  const open = !!detail;
  const [dayClosers, setDayClosers] = React.useState([]);

  React.useEffect(() => {
    if (!detail?.date) {
      setDayClosers([]);
      return;
    }
    const d = detail.date;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    api
      .get(`/jobs/day-completions?date=${key}`)
      .then((r) => setDayClosers(r.data || []))
      .catch(() => setDayClosers([]));
  }, [detail]);

  const grouped = React.useMemo(() => {
    const list = detail?.jobs || [];
    const buckets = new Map();
    list.forEach((j) => {
      const key = j.team_id || "__none__";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(j);
    });
    const teamName = (id) =>
      teams.find((t) => t.id === id)?.name || tr("Sem equipe");
    return Array.from(buckets.entries())
      .map(([team_id, jobs]) => ({
        team_id,
        team: teams.find((t) => t.id === team_id) || null,
        name: teamName(team_id),
        jobs: jobs.sort((a, b) =>
          a.scheduled_at.localeCompare(b.scheduled_at),
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [detail, teams, tr]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md max-h-[80vh] overflow-y-auto"
        data-testid="day-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {detail?.date.toLocaleDateString("en-US", {
              weekday: "long",
              day: "2-digit",
              month: "long",
            })}{" "}
            <span className="text-sm font-normal text-slate-500">
              · {detail?.jobs.length || 0} {tr("atendimento(s)")}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {dayClosers.length > 0 && (
            <div
              data-testid="day-closed-banner"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
            >
              {dayClosers.map((c) => (
                <div key={c.id} className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="flex-shrink-0" />
                  <span>
                    {tr("Dia fechado por {name}", { name: c.user_name })} ·{" "}
                    {c.job_count} {tr("casa(s)")}
                    {c.created_at
                      ? ` · ${new Date(c.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.team_id} data-testid={`day-team-group-${g.team_id}`}>
              <div className="flex items-center gap-2 mb-1.5 px-1">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: g.team?.color || "#94a3b8" }}
                />
                <span
                  className="text-xs font-bold uppercase tracking-wide"
                  style={{ color: g.team?.color || "#475569" }}
                >
                  {g.name}
                </span>
                <span className="text-[11px] text-slate-400">
                  · {g.jobs.length}
                </span>
              </div>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobListRow
                    key={j.id}
                    job={j}
                    clients={clients}
                    teams={teams}
                    onOpenQuick={onOpenQuick}
                    onMutated={onMutated}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const NO_TEAM_STYLE = {
  head: "#94A3B8",
  bg: "rgba(148,163,184,0.12)",
  border: "rgba(148,163,184,0.35)",
  text: "#475569",
};

// Agrupa os jobs de um dia por equipe, aplicando a cor da equipe (mesmo
// visual do calendário mensal). Equipe removida/sem equipe vai por último.
function groupJobsByTeam(list, teams) {
  const buckets = new Map();
  for (const j of list) {
    const key = j.team_id || "__none__";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(j);
  }
  const rows = Array.from(buckets.entries()).map(([team_id, jobs]) => {
    const team = teams.find((t) => t.id === team_id) || null;
    return {
      team_id,
      name:
        team?.name ||
        (team_id === "__none__" ? "Sem equipe" : "Equipe removida"),
      style: team?.color ? styleFromColor(team.color) : NO_TEAM_STYLE,
      jobs: jobs
        .slice()
        .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    };
  });
  rows.sort((a, b) => {
    if (a.team_id === "__none__") return 1;
    if (b.team_id === "__none__") return -1;
    return a.name.localeCompare(b.name, "pt-BR");
  });
  return rows;
}

function ListByDay({ range, jobs, clients, teams, singleDay, onOpenQuick, onMoved, onDropRequest }) {
  const { t } = useI18n();
  const [dragKey, setDragKey] = useState(null);
  const todayRef = useRef(null);
  const scrolledRef = useRef(false);
  const confirm = useConfirm();
  const todayKey = new Date().toISOString().slice(0, 10);
  const days = [];
  const cur = new Date(range.from);
  while (cur <= range.to) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  // Ao abrir, rola direto até o card do dia de hoje (uma vez).
  useEffect(() => {
    if (scrolledRef.current) return;
    if (todayRef.current) {
      const id = setTimeout(() => {
        todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      scrolledRef.current = true;
      return () => clearTimeout(id);
    }
  });

  async function handleDrop(e, targetDate) {
    e.preventDefault();
    setDragKey(null);
    const jobId = e.dataTransfer.getData("text/job-id");
    const oldIso = e.dataTransfer.getData("text/job-scheduled-at");
    const reqId = e.dataTransfer.getData("text/booking-request-id");
    if (reqId) {
      onDropRequest && onDropRequest(reqId, targetDate);
      return;
    }
    if (!jobId || !oldIso) return;
    const old = new Date(oldIso);
    if (old.toDateString() === targetDate.toDateString()) return;
    const next = new Date(targetDate);
    next.setHours(old.getHours(), old.getMinutes(), 0, 0);
    const mj = jobs.find((x) => x.id === jobId);
    const mt = teams.find((x) => x.id === mj?.team_id);
    if (!(await confirmOffDay(confirm, mj?.team_id, next, mt?.name))) return;
    try {
      await api.patch(`/jobs/${jobId}`, { scheduled_at: next.toISOString() });
      toast.success(
        `${t("Movido para")} ${next.toLocaleDateString("en-US", {
          weekday: "short",
          day: "2-digit",
          month: "short",
        })}`,
      );
      onMoved && onMoved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Falha ao mover"));
    }
  }

  return (
    <div className="space-y-4">
      {days.map((d) => {
        const list = jobs.filter(
          (j) => new Date(j.scheduled_at).toDateString() === d.toDateString(),
        );
        const groups = groupJobsByTeam(list, teams);
        const key = d.toISOString().slice(0, 10);
        const isOver = dragKey === key;
        const isToday = key === todayKey;
        if (singleDay || list.length > 0 || isOver)
          return (
            <div
              ref={isToday ? todayRef : undefined}
              className={`cm-card p-4 transition-colors ${
                isOver
                  ? "ring-2 ring-blue-400 bg-blue-50/60"
                  : isToday
                    ? "ring-2 ring-blue-500 bg-blue-50/50"
                    : ""
              }`}
              key={d.toISOString()}
              data-testid={`list-day-${key}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragKey !== key) setDragKey(key);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setDragKey(null);
              }}
              onDrop={(e) => handleDrop(e, d)}
            >
              <div className="font-semibold mb-3 text-slate-800 flex items-center gap-2">
                {d.toLocaleDateString("en-US", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
                {isToday && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wide"
                    data-testid={`list-today-${key}`}
                  >
                    {t("Hoje")}
                  </span>
                )}
              </div>
              {list.length === 0 ? (
                <div className="text-sm text-slate-500">{t("Sem limpezas.")}</div>
              ) : (
                <div className="space-y-2.5">
                  {groups.map((g) => (
                    <div
                      key={g.team_id}
                      className="rounded-lg overflow-hidden border"
                      style={{ borderColor: g.style.border, background: g.style.bg }}
                      data-testid={`day-teamblock-${key}-${g.team_id}`}
                    >
                      <div
                        className="flex items-center justify-between px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white"
                        style={{ background: g.style.head }}
                      >
                        <span className="truncate">{g.name}</span>
                        <span className="bg-white/25 rounded-full px-1.5 shrink-0">
                          {g.jobs.length}
                        </span>
                      </div>
                      <div className="p-1.5 space-y-1.5">
                        {g.jobs.map((j) => (
                          <JobListRow
                            key={j.id}
                            job={j}
                            clients={clients}
                            teams={teams}
                            hideTeam
                            onOpenQuick={onOpenQuick}
                            onMutated={onMoved}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        return null;
      })}
    </div>
  );
}

function JobDialog({ open, onOpenChange, clients, teams, users, defaultDate, onSaved }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [form, setForm] = useState({
    client_id: "",
    team_id: "",
    scheduled_at: "",
    notes: "",
    priority: "",
  });
  useEffect(() => {
    if (open) {
      const d = new Date(defaultDate);
      d.setHours(9, 0, 0, 0);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setForm({ client_id: "", team_id: "", scheduled_at: iso, notes: "", priority: "" });
    }
  }, [open, defaultDate]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const workdayConflicts = React.useMemo(() => {
    if (!form.team_id || !form.scheduled_at) return [];
    const dow = new Date(form.scheduled_at).getDay();
    const team = teams.find((t) => t.id === form.team_id);
    if (!team) return [];
    const memberIds = team.member_ids || [];
    return memberIds
      .map((uid) => users.find((u) => u.id === uid))
      .filter((u) => u && u.workdays && u.workdays.length > 0 && !u.workdays.includes(dow))
      .map((u) => u.name);
  }, [form.team_id, form.scheduled_at, teams, users]);
  async function save() {
    if (!form.client_id || !form.team_id) {
      toast.error(t("Selecione cliente e equipe"));
      return;
    }
    if (!form.scheduled_at) {
      toast.error(t("Informe a data e o horário do agendamento"));
      return;
    }
    // Block midnight (typically a phantom value when the time picker was skipped)
    const parsed = new Date(form.scheduled_at);
    if (Number.isNaN(parsed.getTime())) {
      toast.error(t("Data/hora inválida"));
      return;
    }
    if (parsed.getHours() === 0 && parsed.getMinutes() === 0) {
      toast.error(t("Informe um horário válido (00:00 não é permitido)"));
      return;
    }
    const payload = {
      ...form,
      priority: form.priority || null,
      scheduled_at: parsed.toISOString(),
    };
    const selTeam = teams.find((x) => x.id === form.team_id);
    if (!(await confirmOffDay(confirm, form.team_id, parsed, selTeam?.name))) return;
    const doPost = async (force) =>
      api.post(`/jobs${force ? "?force=true" : ""}`, payload);
    try {
      await doPost(false);
      toast.success(t("Limpeza agendada"));
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail === "DUPLICATE_DAY") {
        const ok = window.confirm(
          t(
            "Já existe um atendimento para este cliente neste dia. Deseja mesmo repetir?",
          ),
        );
        if (!ok) return;
        try {
          await doPost(true);
          toast.success(t("Limpeza agendada"));
          onSaved();
        } catch (e2) {
          toast.error(formatApiError(e2.response?.data?.detail));
        }
        return;
      }
      toast.error(formatApiError(detail));
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="schedule-job-dialog">
        <DialogHeader>
          <DialogTitle>{t("Agendar limpeza")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Cliente")}>
            
            <select
              data-testid="job-field-client"
              className="cm-input"
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
            >
              <option value="">{t("— selecionar —")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
</FormField>
          <FormField label={t("Equipe")}>
            
            <select
              data-testid="job-field-team"
              className="cm-input"
              value={form.team_id}
              onChange={(e) => set("team_id", e.target.value)}
            >
              <option value="">{t("— selecionar —")}</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
</FormField>
          <FormField label={t("Data e Hora")}>
            
            <input
              data-testid="job-field-date"
              type="datetime-local"
              className="cm-input"
              value={form.scheduled_at}
              onChange={(e) => set("scheduled_at", e.target.value)}
            />
</FormField>
          <FormField label={t("Observações")}>
            
            <textarea
              rows={2}
              className="cm-input"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
</FormField>
          <FormField label={t("Prioridade")} hint={t("Opcional — destaca no app do colaborador")}>
            <select
              data-testid="job-field-priority"
              className="cm-input"
              value={form.priority}
              onChange={(e) => set("priority", e.target.value)}
            >
              <option value="">{t("— Automática (por urgência) —")}</option>
              <option value="high">{t("🔴 Urgente")}</option>
              <option value="medium">{t("🟢 Média")}</option>
              <option value="low">{t("⚪ Baixa")}</option>
            </select>
          </FormField>
          {workdayConflicts.length > 0 && (
            <div
              data-testid="workday-conflict-warning"
              className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm"
            >
              <div className="font-semibold text-amber-900 flex items-center gap-1">
                {t("⚠️ Atenção: dia de folga")}
              </div>
              <div className="text-amber-800 mt-1">
                {workdayConflicts.length === 1
                  ? `${workdayConflicts[0]} ${t("não trabalha")}`
                  : `${workdayConflicts.join(", ")} ${t("não trabalham")}`}{" "}
                {t("no dia escolhido. Você ainda pode agendar, mas confirme com a equipe antes.")}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            onClick={save}
            data-testid="job-save-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Agendar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BacklogPanel({ open, onToggle, requests }) {
  const { t } = useI18n();
  const count = requests.length;
  return (
    <div
      className="hidden lg:block fixed bottom-4 right-4 z-30 w-72 max-w-[92vw]"
      data-testid="schedule-backlog-panel"
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-t-lg shadow-lg border-b font-semibold text-sm ${
          count > 0
            ? "bg-amber-500 text-white border-amber-600"
            : "bg-slate-700 text-white border-slate-800"
        } ${open ? "" : "rounded-b-lg"}`}
        data-testid="schedule-backlog-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <Inbox size={16} />
          {t("Solicitações pendentes")}
          <span className="bg-white/20 px-1.5 rounded text-[11px]">{count}</span>
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>
      {open && (
        <div className="bg-white border border-slate-200 rounded-b-lg shadow-2xl max-h-[60vh] overflow-y-auto">
          {count === 0 ? (
            <div className="p-4 text-xs text-slate-500 text-center">
              {t("Nenhuma solicitação pendente. As novas aparecerão aqui.")}
            </div>
          ) : (
            <div className="p-2 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 px-1">
                {t("Arraste para o calendário para agendar")}
              </div>
              {requests.map((r) => (
                <div
                  key={r.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/booking-request-id", r.id);
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  data-testid={`backlog-request-${r.id}`}
                  className="cursor-grab active:cursor-grabbing border border-amber-200 bg-amber-50 hover:bg-amber-100 rounded-md p-2 text-xs"
                >
                  <div className="font-semibold text-amber-900 truncate">
                    {r.client_name || t("Cliente")}
                  </div>
                  <div className="text-amber-700 text-[11px]">
                    {t("Sugerido:")} {r.preferred_date}
                    {r.preferred_time ? ` · ${to12h(r.preferred_time)}` : ""}
                  </div>
                  {r.notes && (
                    <div className="text-slate-500 text-[10px] mt-1 line-clamp-2">
                      {r.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function DropRequestPicker({ state, teams, clients, onClose, onConfirm }) {
  const { t } = useI18n();
  const open = !!state;
  const req = state?.req;
  const targetDate = state?.targetDate;
  const [teamId, setTeamId] = useState("");
  const [timeStr, setTimeStr] = useState("09:00");

  useEffect(() => {
    if (!open) return;
    setTeamId(teams[0]?.id || "");
    setTimeStr((req?.preferred_time || "").slice(0, 5) || "09:00");
  }, [open, req, teams]);

  if (!open) return null;
  const client = clients.find((c) => c.id === req?.client_id);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="drop-picker-dialog">
        <DialogHeader>
          <DialogTitle>{t("Pré-agendar solicitação")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
            <div className="font-semibold text-amber-900">
              {req?.client_name || client?.name || t("Cliente")}
            </div>
            <div className="text-amber-700 text-xs">
              {t("Solicitado:")} {req?.preferred_date}
              {req?.preferred_time ? ` · ${to12h(req?.preferred_time)}` : ""}
            </div>
            {req?.notes && (
              <div className="text-slate-600 text-xs mt-1">{req.notes}</div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">
              {t("Data alvo")}
            </label>
            <div className="text-slate-800 font-medium">
              {targetDate?.toLocaleDateString("en-US", {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              {t("Equipe")}
            </label>
            <select
              data-testid="drop-picker-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            >
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              {t("Horário")}
            </label>
            <TimePicker12h
              testId="drop-picker-time"
              value={timeStr}
              onChange={(v) => setTimeStr(v)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="drop-picker-cancel">
              {t("Cancelar")}
            </Button>
            <Button
              onClick={() => onConfirm({ teamId, timeStr })}
              data-testid="drop-picker-confirm"
              disabled={!teamId}
            >
              {t("Agendar")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
